import { SecuritySymbolHelper, SymbolType, type SecuritySymbol } from '../../domain/symbol.domain';
import { MarketType } from '../../domain/market.domain';

/**
 * Validates symbol input according to configured rules.
 *
 * Consolidates all symbol validation logic into a single, testable service.
 * Replaces the previous scattered `isValidSymbol()` helper logic while keeping
 * component interaction semantics unchanged.
 */
export class SymbolValidator {
  /**
   * Create a validator with specific rules.
   *
   * @param market - Optional market constraint (e.g., 'CA', 'US')
   * @param symbolTypes - Allowed symbol types (STOCK, ETF, OPTION, INDEX, MUTUALFUND)
   */
  constructor(
    private readonly market?: MarketType,
    private readonly symbolTypes?: SymbolType | Array<SymbolType>
  ) {}

  /**
   * Check if a symbol string is valid according to configured rules (strict check only).
   *
   * Returns true only if the input:
   * - Is not empty
   * - Passes SecuritySymbolHelper.isValid() check
   * - Matches market constraint (if configured)
   *
   * @param input - Symbol string to validate
   * @returns true if valid, false otherwise
   */
  isValid(input: string | null | undefined): boolean {
    if (!input || input.trim() === '') {
      return false;
    }

    const isValid = SecuritySymbolHelper.isValid(input, this.symbolTypes);
    if (!isValid) {
      return false;
    }

    // Check market constraint
    if (this.market !== undefined) {
      try {
        const parsed = SecuritySymbolHelper.parse<SecuritySymbol>(input);
        if (parsed.market !== this.market) {
          return false;
        }
      } catch {
        return false;
      }
    }

    return true;
  }
}

